import React from "react";
import {Badge, Table} from "react-bootstrap";
import {Link} from "@inertiajs/react";
import AnalysisCard from "./AnalysisCard.jsx";
import CustomerNumberCell from "./CustomerNumberCell.jsx";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import {statusLabel, statusVariant} from "./zustaendigkeitStatus.js";
import {formatEuroWhole, hasAbility} from "./format.js";
import {StickyTableArea} from "../StickyFilterBlock.jsx";

/**
 * Tabelle der Differenzansicht: Kundennummer prominent, Vorlage-Kürzel
 * (Fassung Oscar, dazu die 2024er Historie klein darunter, wenn sie
 * abweicht), weclapp-Zuständiger (Kürzel, sonst die rohe weclapp-User-ID),
 * Status und Umsatz Vorjahr/laufendes Jahr. Reine Anzeige - das Ändern der
 * Zuständigkeit (Rückschreiben nach weclapp) folgt in einem späteren
 * Schritt. Der Name verlinkt nur mit dem Recht `abilities.kunde` auf die
 * Kundenseite, sonst bleibt er reiner Text.
 */
export default function ZustaendigkeitTable({rows, loading, error, meta}) {
    const canLinkCustomer = hasAbility(meta, "kunde");

    return (
        <AnalysisCard
            title="Kunden"
            help="Kundenweise Gegenüberstellung von Vorlage und weclapp."
            loading={loading}
            error={error}
            empty={!loading && rows.length === 0}
        >
            <StickyTableArea>
                <Table size="sm" hover className="mb-0 align-middle">
                    <thead className="sticky-top bg-white">
                        <tr>
                            <th>Kunde</th>
                            <th>
                                <HeaderWithHelp help="Kürzel aus dem Blatt ‚Ansprechpartner' der Excel-Auswertung (Fassung 09.09.2026).">
                                    Vorlage
                                </HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Kundenverantwortlicher im weclapp-Kundenstamm (nachts gespiegelt).">
                                    weclapp
                                </HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Vergleich zwischen Vorlage und weclapp; siehe Kachel-Erklärungen oben.">
                                    Status
                                </HeaderWithHelp>
                            </th>
                            <th className="text-end">Umsatz Vorjahr</th>
                            <th className="text-end">Umsatz lfd. Jahr</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={row.customer_number || `ohne-nummer-${index}`}>
                                <td>
                                    <CustomerNumberCell customerNumber={row.customer_number || "–"} legacyNumbers={row.legacy_numbers} />
                                    {row.name && row.customer_number && (
                                        <div>
                                            {canLinkCustomer ? (
                                                <Link href={`/vertrieb/kunde/${row.customer_number}`}>{row.name}</Link>
                                            ) : (
                                                row.name
                                            )}
                                        </div>
                                    )}
                                    {row.name && !row.customer_number && <div>{row.name}</div>}
                                    {!row.name && !row.im_weclapp_kundenstamm && (
                                        <div className="small text-muted">nicht im weclapp-Kundenstamm</div>
                                    )}
                                    {row.ausgeschlossen && (
                                        <div className="small text-muted">
                                            <Badge bg="light" text="dark" className="border">
                                                von der Auswertung ausgeschlossen{row.excluded_reason ? `: ${row.excluded_reason}` : ""}
                                            </Badge>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {row.vorlage_code || "–"}
                                    {row.vorlage_code_2024 && row.vorlage_code_2024 !== row.vorlage_code && (
                                        <div className="small text-muted">2024: {row.vorlage_code_2024}</div>
                                    )}
                                </td>
                                <td>
                                    {row.weclapp_code
                                        || (row.weclapp_user_name ? `${row.weclapp_user_name} (kein Kürzel)` : null)
                                        || (row.weclapp_user_id_raw ? `weclapp-Benutzer ${row.weclapp_user_id_raw}` : "–")}
                                </td>
                                <td><Badge bg={statusVariant(row.status)}>{statusLabel(row.status)}</Badge></td>
                                <td className="text-end">{formatEuroWhole(row.umsatz_vorjahr)}</td>
                                <td className="text-end">{formatEuroWhole(row.umsatz_laufend)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </StickyTableArea>
        </AnalysisCard>
    );
}
